/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { type Action, action, computed, type Computed } from 'easy-peasy'
import type { BillEntryData } from '@strata-monorepo/shared-ui/components/BillEntry/BillEntry'
import { uploadedBillsModel, type UploadedBillsModel } from './uploadedBillsModel'
import { manualBillsModel, type ManualBillsModel } from './manualBillsModel'

const REQUIRED_FIELDS_MANIFEST = ['usage', 'month', 'year'] as const

export interface BillModel {
  // States
  manualEntryToggle: boolean
  requiredFieldsManifest: readonly string[]

  // Nested Store Models
  uploadedBillsModel: UploadedBillsModel
  manualBillsModel: ManualBillsModel

  // Actions
  setManualEntryToggle: Action<BillModel, boolean>

  // Computed
  reconciledEntries: Computed<BillModel, BillEntryData[]>
}

export const billModel = (): BillModel => ({
  // States
  manualEntryToggle: false,
  requiredFieldsManifest: REQUIRED_FIELDS_MANIFEST,

  // Nested Store Models
  uploadedBillsModel: uploadedBillsModel(),

  manualBillsModel: manualBillsModel(),

  // Actions
  setManualEntryToggle: action((state, payload) => {
    state.manualEntryToggle = payload
  }),

  // Computed
  reconciledEntries: computed((state) => {
    if (!state.manualEntryToggle) {
      return state.uploadedBillsModel.uploadedData.filter((entry) =>
        hasRequiredFields(entry, state.requiredFieldsManifest),
      )
    }

    const reconciled = reconcileByMonthYear(
      state.uploadedBillsModel.uploadedData,
      state.manualBillsModel.manualData,
    )

    return reconciled.filter((entry) => hasRequiredFields(entry, state.requiredFieldsManifest))
  }),
})

const countFilledFields = (entry: BillEntryData): number => {
  return Object.values(entry).filter((value) => {
    if (typeof value === 'string') {
      return value.trim() !== ''
    }
    return value !== null && value !== undefined
  }).length
}

const normalizeMonthYear = (month: string, year: number | null): string => {
  if (!year || year === null) return month.trim().toLowerCase()

  const yearString = year.toString()

  return `${month.trim().toLowerCase()}-${yearString}`
}

const hasRequiredFields = (entry: BillEntryData, manifest: readonly string[]): boolean => {
  return manifest.every((fieldName) => {
    const value = entry[fieldName as keyof BillEntryData]
    if (typeof value === 'string') {
      return value.trim() !== ''
    }
    if (typeof value === 'number') {
      return value > 0
    }
    return value !== null && value !== undefined
  })
}

const reconcileByMonthYear = (
  ocrEntries: BillEntryData[],
  manualEntries: BillEntryData[],
): BillEntryData[] => {
  const grouped = new Map<string, BillEntryData[]>()

  ocrEntries.forEach((entry) => {
    const key = normalizeMonthYear(entry.month, entry.year)
    if (!grouped.has(key)) {
      grouped.set(key, [])
    }
    grouped.get(key)!.push(entry)
  })

  manualEntries.forEach((entry) => {
    const key = normalizeMonthYear(entry.month, entry.year)
    if (!grouped.has(key)) {
      grouped.set(key, [])
    }
    grouped.get(key)!.push(entry)
  })

  const reconciled: BillEntryData[] = []

  grouped.forEach((entries) => {
    if (entries.length === 1) {
      reconciled.push(entries[0]!)
    } else {
      // Most filled fields wins, manual breaks ties
      let winner = entries[0]!
      let maxFields = countFilledFields(winner)
      const isWinnerFromManual = manualEntries.includes(winner)

      for (let i = 1; i < entries.length; i++) {
        const candidate = entries[i]!
        const candidateFields = countFilledFields(candidate)
        const isCandidateFromManual = manualEntries.includes(candidate)

        if (candidateFields > maxFields) {
          winner = candidate
          maxFields = candidateFields
        } else if (candidateFields === maxFields && isCandidateFromManual && !isWinnerFromManual) {
          winner = candidate
        }
      }

      reconciled.push(winner)
    }
  })

  return reconciled
}
